import React, { useState, useMemo } from "react";
import { Box, Button, Divider, Grid, Typography, Hidden } from "@mui/material";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  Add,
  Delete,
  PlayCircleFilled,
  ExitToApp,
  Launch
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { push } from "connected-react-router";
import {
  buildCourseBuilderContentsRoute,
  buildCourseDetailsVersionsRoute
} from "../../../../routes/AdminRoutes";
import { contentHosts } from "../../../../constants";
import CourseOutline from "../../../../components/CourseOutline/CourseOutline";
import ButtonGroup from "../../../../components/Buttons/ButtonGroup";
import {
  createRelease,
  validateRelease,
  deleteRelease,
  updateCourseVersionData,
  fetchCourseVersionData
} from "../../../../redux/actions/contentCoursesActions";
import OnlineCourseForm from "../../../../components/Forms/OnlineCourseForm";
import { filesPreloading } from "../../../../services/fileUploadService";
import WarningModal from "../../../../components/WarningModal/WarningModal";
import { buildCoursePreviewRoute } from "../../../../routes/Routes";
import LoadingButton from "../../../../components/Buttons/LoadingButton";
import DeploymentModals from "./DeploymentModals";
import Alert from "@mui/material/Alert";
import { useCourseSections } from "../../../../hooks/courses";

const firstReleaseVersion = "1.0";

const ProcessingVideoNote = ({ processingVideos }) => {
  const { t } = useTranslation();
  const vidMsg = (
    <Box mb={2}>
      <Alert severity="info">
        {t("videoProcessing.deploy", {
          title: processingVideos
            .map(processingVideo => processingVideo.title)
            .join(", ")
        })}
      </Alert>
    </Box>
  );
  return vidMsg;
};

const CourseVersionDetailsButtons = ({
  releasePending,
  releaseFirst,
  onPreviewClick,
  onCreateReleaseClick,
  onDeleteReleaseClick,
  onGoToCourseBuilderClick,
  onDeploy,
  onSaveClick,
  disabled,
  isDeploying,
  processingVideos
}) => {
  const { t } = useTranslation();
  const getReleaseButton = () => {
    if (!releasePending) {
      return (
        <Box display="flex">
          <LoadingButton
            pending={isCreatingNewRelease}
            startIcon={<Add />}
            disabled={disabled}
            size="large"
            color="primary"
            onClick={onCreateReleaseClick}
          >
            {t("courseVersion.details.createBtn")}
          </LoadingButton>
        </Box>
      );
    }
    if (!releaseFirst) {
      return (
        <Button
          startIcon={<Delete />}
          onClick={onDeleteReleaseClick}
          color="primary"
          size="large"
          disabled={disabled}
        >
          {t("courseVersion.details.deleteBtn")}
        </Button>
      );
    }
  };

  const { isCreatingNewRelease } = useSelector(
    ({ contentCoursesState: { isCreatingNewRelease } }) => ({
      isCreatingNewRelease
    })
  );

  return (
    <>
      {processingVideos.length ? (
        <ProcessingVideoNote processingVideos={processingVideos} />
      ) : (
        <></>
      )}
      <Box
        display="flex"
        justifyContent="space-between"
        mb={3}
        flexDirection={{ xs: "column", sm: "row" }}
        flexWrap="wrap"
        alignItems="flex-start"
      >
        <ButtonGroup align="left" flexWrap="wrap">
          <Button
            startIcon={<Launch />}
            onClick={onPreviewClick}
            color="primary"
            size="large"
            disabled={disabled}
          >
            {t("courseVersion.details.previewBtn")}
          </Button>
          {getReleaseButton()}
          {releasePending && (
            <>
              <Button
                startIcon={<ExitToApp />}
                onClick={onGoToCourseBuilderClick}
                color="primary"
                size="large"
                disabled={disabled}
              >
                {t("courseVersion.details.courseBuilderBtn")}
              </Button>
              <LoadingButton
                pending={isDeploying}
                startIcon={<PlayCircleFilled />}
                size="large"
                color="primary"
                onClick={onDeploy}
                data-qa="course-version-deploy-btn"
                disabled={processingVideos.length || disabled}
              >
                {t("courseVersion.details.deployBtn")}
              </LoadingButton>
            </>
          )}
        </ButtonGroup>
        <Button
          variant="contained"
          color="primary"
          onClick={() => onSaveClick()}
          disabled={disabled}
        >
          {t("saveChangesBtn")}
        </Button>
      </Box>
    </>
  );
};

CourseVersionDetailsButtons.propTypes = {
  onCreateReleaseClick: PropTypes.func,
  onDeleteReleaseClick: PropTypes.func,
  onDeploy: PropTypes.func,
  onPreviewClick: PropTypes.func,
  onGoToCourseBuilderClick: PropTypes.func,
  onSaveClick: PropTypes.func,
  disabled: PropTypes.bool,
  isDeploying: PropTypes.bool,
  releasePending: PropTypes.bool,
  releaseFirst: PropTypes.bool
};

const CourseVersionDetailsInfo = ({
  courseVersionType,
  currentReleaseVersion,
  recordsScore,
  passingScore,
  releasePending
}) => {
  const { t } = useTranslation();
  return (
    <Grid container spacing={3}>
      <Grid item xs={6}>
        <Typography variant="subtitle1" color="textSecondary">
          {t("courseVersion.details.type")}
        </Typography>
      </Grid>
      <Grid item xs={6}>
        <Typography variant="subtitle1" id="courseVersionType">
          {courseVersionType}
        </Typography>
      </Grid>
      <Grid item xs={6}>
        <Typography variant="subtitle1" color="textSecondary">
          {t("courseVersion.details.release")}
        </Typography>
      </Grid>
      <Grid item xs={6}>
        <Typography variant="subtitle1" id="currentReleaseVersion">
          {releasePending
            ? t("courseVersion.details.pendingRelease", {
                releaseVersion: currentReleaseVersion
              })
            : currentReleaseVersion}
        </Typography>
      </Grid>
      <Grid item xs={6}>
        <Typography variant="subtitle1" color="textSecondary">
          {t("courseVersion.details.passingScore")}
        </Typography>
      </Grid>
      <Grid item xs={6}>
        {recordsScore && (
          <Typography
            variant="subtitle1"
            id="passingScore"
          >{`${passingScore}%`}</Typography>
        )}
      </Grid>
    </Grid>
  );
};

CourseVersionDetailsInfo.propTypes = {
  courseVersionType: PropTypes.string,
  recordsScore: PropTypes.bool,
  passingScore: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  currentReleaseVersion: PropTypes.string,
  releasePending: PropTypes.bool
};

const CourseVersionDetails = ({
  versionData: {
    pending,
    type,
    trainingHours,
    trainingMinutes,
    description,
    name,
    status,
    language,
    release,
    passingScore,
    recordScore,
    contentHost,
    showOverview,
    overviewText,
    courseContent,
    id: currentVersionId
  },
  match: {
    params: { courseId, versionId }
  }
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [overviewFiles, setOverviewFiles] = useState([]);
  const [isDeploying, setIsDeploying] = useState(false);
  const [warningMessage, setWarningMessage] = useState(null);

  const { isUpdatingCourseVersion } = useSelector(
    ({ contentCoursesState: { isUpdatingCourseVersion } }) => ({
      isUpdatingCourseVersion
    })
  );
  const {
    fetchCourseSections,
    mandatorySections,
    optionalSections
  } = useCourseSections(versionId);
  const brsp = contentHost === contentHosts.brsp;
  const isFirstRelease = release === firstReleaseVersion;

  const handleGoToCourseBuilderClick = () => {
    dispatch(push(buildCourseBuilderContentsRoute(courseId, versionId)));
  };

  const handleSaveVersionDetails = async values => {
    const tmpObj = await filesPreloading(overviewFiles, values.overviewText);
    values.overviewMedia.images = tmpObj.images;
    values.overviewMedia.media = tmpObj.media;
    values.overviewMedia.attachments = tmpObj.attachments;
    values.overviewMedia.content = tmpObj.content;
    return dispatch(
      updateCourseVersionData(courseId, versionId, values, () => {
        if (status === "Active" && values.status === "Inactive") {
          handleDetailsUpdate();
        } else {
          fetchCourseSections();
        }
      })
    );
  };

  const handleDetailsUpdate = () => {
    dispatch(push(buildCourseDetailsVersionsRoute(courseId)));
  };

  const handleCreateReleaseClick = () => {
    dispatch(createRelease(courseId, versionId));
  };

  const warningMessageText = affectedCourses => {
    if (!affectedCourses || affectedCourses.length === 0) {
      return t("courseVersion.details.pendingItemsMessageWarning", {
        insertion: ""
      });
    }
    return t("courseVersion.details.pendingItemsMessageWarning", {
      insertion: t("courseVersion.details.affectedCoursesMessageWarning", {
        affectedCourses: affectedCourses.join(", ")
      })
    });
  };

  const onDeleteRelease = () => {
    dispatch(
      deleteRelease(courseId, versionId, () => {
        dispatch(fetchCourseVersionData(courseId, versionId));
        setWarningMessage(null);
      })
    ).then(() => fetchCourseSections());
  };

  const handleDeleteReleaseClick = () => {
    dispatch(
      validateRelease(courseId, versionId, validationResult => {
        if (validationResult.hasPendingActivities) {
          setWarningMessage({
            open: true,
            message: warningMessageText(validationResult.affectedCourses)
          });
        } else {
          onDeleteRelease();
        }
      })
    );
  };

  const handleDeployClick = () => {
    setIsDeploying(true);
  };

  const handlePreviewCourse = () => {
    window.open(buildCoursePreviewRoute(versionId));
  };

  const processingVideos = useMemo(() => {
    if (!courseContent) return [];
    const processing = courseContent.reduce(
      (processingItems, section) => [
        ...processingItems,
        ...section.items.filter(item => item.processing)
      ],
      []
    );
    return processing;
  }, [courseContent]);

  const initialVersionData = useMemo(
    () => ({
      name: name ? name : "",
      description: description ? description : "",
      language: language ? language : "English",
      status: status,
      trainingHours: trainingHours ? trainingHours : 0,
      trainingMinutes: trainingMinutes ? trainingMinutes : 0,
      showOverview: !!showOverview,
      overviewText: overviewText
        ? overviewText
        : t("manageCourses.defaultCourseOverview"),
      overviewTextOnlyLength: 0,
      overviewMedia: {}
    }),
    [currentVersionId]
  );

  return (
    <OnlineCourseForm
      initialValues={initialVersionData}
      onSubmit={async values => {
        delete values.overviewTextOnlyLength;
        handleSaveVersionDetails(values);
      }}
      disabled={isUpdatingCourseVersion || isDeploying}
      render={({
        formFields,
        formikProps: { submitForm, isSubmitting, values }
      }) => (
        <Box display="flex" flexDirection="column" pt={3}>
          <WarningModal
            open={!!(warningMessage && warningMessage.open)}
            onClose={() => setWarningMessage(null)}
            onSubmit={() => onDeleteRelease()}
            warningMessage={warningMessage ? warningMessage.message : ""}
          />
          {!brsp && (
            <CourseVersionDetailsButtons
              onGoToCourseBuilderClick={handleGoToCourseBuilderClick}
              onCreateReleaseClick={handleCreateReleaseClick}
              onDeleteReleaseClick={handleDeleteReleaseClick}
              releasePending={pending}
              releaseFirst={isFirstRelease}
              onDeploy={() => handleDeployClick(values)}
              onPreviewClick={handlePreviewCourse}
              disabled={isSubmitting || isUpdatingCourseVersion || isDeploying}
              isDeploying={isDeploying}
              processingVideos={processingVideos}
              onSaveClick={submitForm}
            />
          )}
          <DeploymentModals
            isDeploying={isDeploying}
            newVersionData={values}
            versionId={versionId}
            courseId={courseId}
            isPendingRelease={pending}
            isFirstRelease={release === firstReleaseVersion}
            onSaveVersionDetails={() => handleSaveVersionDetails(values)}
            onDeployEnd={() => setIsDeploying(false)}
          />
          <Box display="flex" flexDirection={{ xs: "column", md: "row " }}>
            <Box display="flex" flexDirection="column" flex={3}>
              <Box>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    {formFields.name}
                  </Grid>
                  <Grid item xs={12}>
                    <Box my={2}>{formFields.description}</Box>
                  </Grid>
                  <Grid item xs={12}>
                    {formFields.language}
                  </Grid>
                  <Grid item xs={12}>
                    {formFields.status}
                  </Grid>
                  <Grid item xs={12} lg={6}>
                    {formFields.trainingHours}
                  </Grid>
                  <Grid item xs={12} lg={6}>
                    {formFields.trainingMinutes}
                  </Grid>
                </Grid>
              </Box>
              <Box my={5}>
                <CourseVersionDetailsInfo
                  courseVersionType={type}
                  currentReleaseVersion={release}
                  recordsScore={recordScore}
                  passingScore={passingScore}
                  releasePending={pending}
                />
              </Box>
            </Box>
            <Hidden smDown>
              <Divider
                orientation="vertical"
                sx={{ height: "unset", my: 0, mx: 4 }}
              />
            </Hidden>
            <Box flex={5}>
              <CourseOutline
                courseContent={mandatorySections}
                resources={optionalSections}
                brsp={brsp}
                hideOverviewCheckbox={contentHost !== contentHosts.visionHosted}
                onFilesListChange={filesList => {
                  setOverviewFiles(filesList);
                }}
                filesList={overviewFiles}
                disableFields={isUpdatingCourseVersion || isDeploying}
              />
            </Box>
          </Box>
        </Box>
      )}
    />
  );
};

export default CourseVersionDetails;

CourseVersionDetails.propTypes = {
  versionData: PropTypes.shape({
    type: PropTypes.string,
    recordScore: PropTypes.bool,
    passingScore: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    release: PropTypes.string,
    pending: PropTypes.bool,
    language: PropTypes.string,
    trainingHours: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    trainingMinutes: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    status: PropTypes.string,
    description: PropTypes.string,
    name: PropTypes.string,
    courseContent: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        items: PropTypes.arrayOf(
          PropTypes.shape({
            type: PropTypes.string,
            title: PropTypes.string
          })
        )
      })
    ),
    resources: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        items: PropTypes.arrayOf(
          PropTypes.shape({
            type: PropTypes.string,
            title: PropTypes.string
          })
        )
      })
    )
  })
};
